/**
 * Durable client-side registry of hidden chat messages.
 *
 * The `metadata.hidden` flag hides a message live, but it is NOT persisted by Mastra
 * memory for user-input messages — so after a turn finishes (the chat session remounts
 * from server history) or after a reload, the restored message loses the flag and would
 * render as a normal bubble.
 *
 * This registry keeps hidden messages hidden across those round-trips without putting any
 * marker in the message text (the model never sees anything extra) and without a backend
 * change. We remember two keys per hidden message:
 *  - `id:<messageId>`   — matches when Mastra preserves the client-sent message id.
 *  - `tx:<hash(text)>`  — fallback that always matches, since the message text is persisted
 *                         verbatim and restored faithfully.
 */

const STORAGE_KEY = 'ai:hidden-message-keys';
/** Cap the registry so it can't grow unbounded across many sessions. */
const MAX_KEYS = 300;

/** djb2 — small, stable, non-cryptographic. Only needs to avoid collisions across prompts. */
const hashText = (text: string): string => {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 33) ^ text.charCodeAt(i);
  }
  // >>> 0 keeps it an unsigned 32-bit int.
  return (hash >>> 0).toString(36);
};

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage;

const readKeys = (): string[] => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

const writeKeys = (keys: string[]): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys.slice(-MAX_KEYS)));
  } catch {
    // Ignore quota / privacy-mode errors — the live metadata filter still hides the message this session.
  }
};

const buildKeys = ({ id, text }: { id: string; text: string }): string[] => {
  const keys: string[] = [];
  if (id) keys.push(`id:${id}`);
  const trimmed = text.trim();
  if (trimmed) keys.push(`tx:${hashText(trimmed)}`);
  return keys;
};

/** Record a message so it stays hidden after remount/reload. Call right after sending it. */
export const rememberHiddenMessage = (message: { id: string; text: string }): void => {
  const newKeys = buildKeys(message);
  if (newKeys.length === 0) return;
  const merged = new Set(readKeys());
  for (const key of newKeys) merged.add(key);
  writeKeys([...merged]);
};

/** True when a message was previously flagged hidden (by id or by text). */
export const isRememberedHiddenMessage = (message: { id: string; text: string }): boolean => {
  const keys = new Set(readKeys());
  return buildKeys(message).some((key) => keys.has(key));
};
